import type {
  ChordPlacement,
  ChordQuality,
  ChordSpec,
  LayoutKey,
  VirtuosaLayout,
} from './types';
import { MAX_HAND_STRETCH } from './layout';

export const CHORD_INTERVALS: Record<ChordQuality, number[]> = {
  maj: [0, 4, 7],
  min: [0, 3, 7],
  dim: [0, 3, 6],
  aug: [0, 4, 8],
  '7': [0, 4, 7, 10],
  maj7: [0, 4, 7, 11],
  m7: [0, 3, 7, 10],
  m7b5: [0, 3, 6, 10],
  dim7: [0, 3, 6, 9],
};

function handStretch(keys: LayoutKey[]): number {
  if (keys.length < 2) {
    return 0;
  }

  let max = 0;
  for (let i = 0; i < keys.length; i++) {
    for (let j = i + 1; j < keys.length; j++) {
      const d = Math.hypot(keys[i].x - keys[j].x, keys[i].y - keys[j].y);
      if (d > max) {
        max = d;
      }
    }
  }

  return max;
}

function pickMostCompact(opts: LayoutKey[][]): LayoutKey[] {
  let best: LayoutKey[] = [];
  let bestSpread = Infinity;

  const recurse = (depth: number, picked: LayoutKey[]): void => {
    if (depth === opts.length) {
      const s = handStretch(picked);
      if (s < bestSpread) {
        bestSpread = s;
        best = picked.slice();
      }

      return;
    }

    for (const k of opts[depth]) {
      picked.push(k);
      recurse(depth + 1, picked);
      picked.pop();
    }
  };

  recurse(0, []);

  return best;
}

function rightmost(keys: LayoutKey[]): LayoutKey {
  let best = keys[0];
  for (const k of keys) {
    if (k.x > best.x) {
      best = k;
    }
  }

  return best;
}

const SHIFT_OPTIONS = [-1, 0, 1] as const;

// Strict root position, against the v2.0 lattice as shipped:
// - Triad goes entirely on right hand, in fundamental position (inv = 0)
// - Bass = root - 12, placed as a single key on left hand (rightmost option)
// - Score = triadStretch + 0.5*|shift| + (fallback ? 3.0 : 0)
// The lattice is pure — no overrides — and guarantees 24/24 maj/min voicings
// in strict root position, 23 of them at stretch 1.80 and A minor alone at
// 3.61, which is what MAX_HAND_STRETCH = 3.7 exists to cover. Inversions are
// never needed and `inversion` is therefore always 0; `placeVoicing` is the
// entry point for a voicing that is not root position.
//
// The 4-override variant that would bring A minor down to 2.00 was priced
// against the catalogue and refused: it costs the monotonicity of row 0 and
// 3 % of scale path to repair one chord. See layout/chord_keyboard_optimizer.md
// § 5.2 for the measurement.
export function placeChord(
  spec: ChordSpec,
  layout: VirtuosaLayout,
): ChordPlacement | null {
  const intervals = CHORD_INTERVALS[spec.quality];
  const baseOctave = spec.preferredOctave ?? 4;
  const rootInBaseOctave = spec.rootSemi + 12 * (baseOctave + 1);

  let best: ChordPlacement | null = null;
  let bestScore = Infinity;

  for (const shift of SHIFT_OPTIONS) {
    const shiftedRoot = rootInBaseOctave + 12 * shift;
    const triad = intervals
      .map((i) => shiftedRoot + i)
      .sort((a, b) => a - b);
    const bassMidi = shiftedRoot - 12;

    if (new Set(triad).size !== triad.length) {
      continue;
    }

    if (triad.some((m) => !layout.byMidi.has(m))) {
      continue;
    }

    const rightOpts = triad.map((m) =>
      (layout.byMidi.get(m) ?? []).filter((k) => k.hand === 'right'),
    );

    if (rightOpts.some((o) => o.length === 0)) {
      continue;
    }

    const triadKeys = pickMostCompact(rightOpts);
    const triadStretch = handStretch(triadKeys);

    if (triadStretch > MAX_HAND_STRETCH) {
      continue;
    }

    let bassKeys: LayoutKey[] = [];
    let hasBass = false;

    if (spec.withBass && layout.byMidi.has(bassMidi)) {
      const bassOpts = (layout.byMidi.get(bassMidi) ?? []).filter(
        (k) => k.hand === 'left',
      );

      if (bassOpts.length > 0) {
        bassKeys = [rightmost(bassOpts)];
        hasBass = true;
      }
    }

    const fallback = spec.withBass && !hasBass;
    const score = triadStretch + 0.5 * Math.abs(shift) + (fallback ? 3.0 : 0);

    if (score < bestScore) {
      bestScore = score;
      best = {
        left: bassKeys,
        right: triadKeys,
        stretch: triadStretch,
        octaveShift: shift,
        inversion: 0,
        hasBass,
        fallback,
      };
    }
  }

  return best;
}
